import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	createWordpressConnection,
	getAllWordpressConnections,
	getWordpressConnectionById,
	updateWordpressConnection,
	deleteWordpressConnection,
	getWordpressConnectionPosts,
	getWordpressConnectionCustomPosts,
	getWordpressConnectionCustomEndpointPosts,
	getWordpressConnectionPostTypes,
	updateWordpressPost,
	getWordpressConnectionPost,
	createWordpressPost,
	deleteWordpressPost,
	saveAllPostTypesToLocalAndAnalyze,
	getLocalContent,
	getWordpressConnectionAISummary,
	triggerWordpressConnectionAnalysis,
	autoGeneratePostsWithAI,
	discoverWordpressEndpoints,
	testWordpressConnection,
	updateWordpressSelectedEndpoints,
	getScheduledPosts,
	getScheduledPostById,
	getScheduledPostSeries,
	updateScheduledPostSeries,
	updateScheduledPostStatus,
	deleteScheduledPost,
	getScheduledPostsStats,
	syncConnectionContent,
	analyzeConnectionContent,
	getConnectionHealth,
	getAiReadiness,
	getDiscoveryCache,
	getMonthlyAiGenerationUsage,
} from '../Api/wordpressConnectionApi';

const CONNECTIONS_KEY = 'wordpressConnections';

// Get all WordPress connections
export const useWordpressConnections = () => {
	const { data, ...rest } = useQuery({
		queryKey: [CONNECTIONS_KEY],
		queryFn: getAllWordpressConnections,
	});
	// Axios returns { data: [...] }
	return { data: data?.data, ...rest };
};

const unwrapWordpressConnectionResponse = (response) =>
	response?.data?.data ?? response?.data ?? response ?? null;

// Get a single WordPress connection by ID
export const useWordpressConnectionById = (id) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, id],
		queryFn: () => getWordpressConnectionById(id),
		enabled: !!id,
		select: unwrapWordpressConnectionResponse,
	});

// Get posts for a specific WordPress connection by ID
export const useWordpressConnectionPosts = (id) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, id, 'posts'],
		queryFn: () => getWordpressConnectionPosts(id),
		enabled: !!id,
	});

// Get available post types for a WordPress connection
export const useWordpressConnectionPostTypes = (id) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, id, 'types'],
		queryFn: () => getWordpressConnectionPostTypes(id),
		enabled: !!id,
	});

// Get custom posts of a specific type for a WordPress connection by ID
export const useWordpressConnectionCustomPosts = (id, type) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, id, 'custom', type],
		queryFn: () => getWordpressConnectionCustomPosts(id, type),
		enabled: !!id && !!type,
	});

// Get posts using a custom endpoint URL for a WordPress connection
export const useWordpressConnectionCustomEndpointPosts = (id, endpointUrl) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, id, 'custom-endpoint', endpointUrl],
		queryFn: () => getWordpressConnectionCustomEndpointPosts(id, endpointUrl),
		enabled: !!id && !!endpointUrl,
	});

// Create a new WordPress connection
export const useCreateWordpressConnection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createWordpressConnection,
		onSuccess: () => {
			queryClient.invalidateQueries([CONNECTIONS_KEY]);
		},
	});
};

// Update a WordPress connection
export const useUpdateWordpressConnection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }) => updateWordpressConnection(id, data),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries([CONNECTIONS_KEY]);
			queryClient.invalidateQueries([CONNECTIONS_KEY, id]);
		},
	});
};

// Delete a WordPress connection
export const useDeleteWordpressConnection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteWordpressConnection,
		onSuccess: () => {
			queryClient.invalidateQueries([CONNECTIONS_KEY]);
		},
	});
};

// Update a specific WordPress post
export const useUpdateWordpressPost = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ connectionId, postType, postId, updateData }) =>
			updateWordpressPost(connectionId, postType, postId, updateData),
		onSuccess: (_, variables) => {
			const { connectionId, postType } = variables;
			// Invalidate queries to refresh the data
			queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId, 'posts']);
			queryClient.invalidateQueries([
				CONNECTIONS_KEY,
				connectionId,
				'custom',
				postType,
			]);
		},
	});
};

// Create a new WordPress post
export const useCreateWordpressPost = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ connectionId, postType, createData }) =>
			createWordpressPost(connectionId, postType, createData),
		onSuccess: (_, variables) => {
			const { connectionId, postType } = variables;
			queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId, 'posts']);
			queryClient.invalidateQueries([
				CONNECTIONS_KEY,
				connectionId,
				'custom',
				postType,
			]);
		},
	});
};

// Delete a specific WordPress post
export const useDeleteWordpressPost = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ connectionId, postType, postId }) =>
			deleteWordpressPost(connectionId, postType, postId),
		onSuccess: (_, variables) => {
			const { connectionId, postType } = variables;
			queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId, 'posts']);
			queryClient.invalidateQueries([
				CONNECTIONS_KEY,
				connectionId,
				'custom',
				postType,
			]);
		},
	});
};

// Fetch a single post for a specific WordPress connection by ID and post type
export const useWordpressConnectionPost = (connectionId, postType, postId) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, connectionId, postType, 'post', postId],
		queryFn: () =>
			getWordpressConnectionPost(connectionId, postType, postId).then(
				(res) => res.data,
			),
		enabled: !!connectionId && !!postType && !!postId,
	});

// Save all posts of a post type to local DB
export const useSaveAllPostTypesToLocalAndAnalyze = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ connectionId, postTypes }) =>
			saveAllPostTypesToLocalAndAnalyze(connectionId, postTypes),
		onSuccess: (_, { connectionId }) => {
			queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, connectionId] });
			queryClient.invalidateQueries({ queryKey: ['localContent', connectionId] });
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-readiness'],
			});
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-summary'],
			});
		},
	});
};

// Get all locally saved posts for a connection (optionally by postType)
export const useLocalContent = (connectionId, postType) =>
	useQuery({
		queryKey: ['localContent', connectionId, postType],
		queryFn: () =>
			getLocalContent(connectionId, postType).then((res) => res.data),
		enabled: !!connectionId,
	});

// Get AI summary for a specific WordPress connection by ID
export const useWordpressConnectionAISummary = (connectionId) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, connectionId, 'ai-summary'],
		queryFn: () =>
			getWordpressConnectionAISummary(connectionId).then((res) => res.data),
		enabled: !!connectionId,
	});

// Trigger AI analysis for a WordPress connection
export const useTriggerWordpressConnectionAnalysis = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (connectionId) =>
			triggerWordpressConnectionAnalysis(connectionId).then((res) => res.data),

		onSuccess: (_, connectionId) => {
			// Invalidate and refetch the AI summary to get the updated data
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-summary'],
			});
		},
	});
};

// Auto-generate posts with AI
export const useAutoGeneratePostsWithAI = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ connectionId, data, topicImages = {} }) =>
			autoGeneratePostsWithAI(connectionId, data, topicImages),

		onSuccess: (_, variables) => {
			const { connectionId } = variables;

			// Invalidate relevant queries to refresh the data
			queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId]);

			// If you have a scheduled posts query to refresh
			queryClient.invalidateQueries(['scheduledPosts', connectionId]);
			queryClient.invalidateQueries(['aiWordpressGenerationUsage']);

			// You might also want to invalidate the AI summary if it's affected
			queryClient.invalidateQueries([
				CONNECTIONS_KEY,
				connectionId,
				'ai-summary',
			]);
		},
	});
};

// Discover available endpoints for a WordPress connection
export const useDiscoverWordpressEndpoints = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (connectionData) => {
			// If connectionData is an object with connection details, use test endpoint
			if (typeof connectionData === 'object' && connectionData.siteUrl) {
				return testWordpressConnection(connectionData);
			}
			// Otherwise, assume it's a connection ID
			return discoverWordpressEndpoints(connectionData);
		},
		onSuccess: (_, connectionId) => {
			// Only invalidate if connectionId is a string (existing connection)
			if (typeof connectionId === 'string') {
				queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId]);
				queryClient.invalidateQueries([CONNECTIONS_KEY]);
			}
		},
	});
};

// Update selected endpoints for a WordPress connection
export const useUpdateWordpressSelectedEndpoints = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ connectionId, selectedEndpoints }) =>
			updateWordpressSelectedEndpoints(connectionId, selectedEndpoints),
		onSuccess: (response, { connectionId, selectedEndpoints }) => {
			const updatedEndpoints =
				response?.data?.data?.selectedEndpoints ??
				response?.data?.selectedEndpoints ??
				selectedEndpoints;

			if (updatedEndpoints) {
				queryClient.setQueryData([CONNECTIONS_KEY, connectionId], (existing) => {
					if (!existing) return existing;

					if (existing?.data?.data) {
						return {
							...existing,
							data: {
								...existing.data,
								data: {
									...existing.data.data,
									selectedEndpoints: updatedEndpoints,
								},
							},
						};
					}

					if (existing?.data) {
						return {
							...existing,
							data: {
								...existing.data,
								selectedEndpoints: updatedEndpoints,
							},
						};
					}

					return { ...existing, selectedEndpoints: updatedEndpoints };
				});
			}

			queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, connectionId] });
			queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY] });
		},
	});
};

// Scheduled Posts Hooks
const SCHEDULED_POSTS_KEY = 'scheduledPosts';
const AI_GENERATION_USAGE_KEY = 'aiWordpressGenerationUsage';

export const useAiWordpressMonthlyUsage = (companyId) =>
	useQuery({
		queryKey: [AI_GENERATION_USAGE_KEY, 'monthly-usage', companyId],
		queryFn: () => getMonthlyAiGenerationUsage(companyId),
		enabled: Boolean(companyId),
		select: (response) => response?.data?.data ?? response?.data ?? null,
	});

// Get scheduled posts for a connection
export const useScheduledPosts = (connectionId) =>
	useQuery({
		queryKey: [SCHEDULED_POSTS_KEY, connectionId],
		queryFn: () => getScheduledPosts(connectionId),
		enabled: !!connectionId,
	});

// Get a single scheduled post by ID
export const useScheduledPostById = (postId) =>
	useQuery({
		queryKey: [SCHEDULED_POSTS_KEY, 'single', postId],
		queryFn: () => getScheduledPostById(postId),
		enabled: !!postId,
	});

// Update scheduled post status
export const useUpdateScheduledPostStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ postId, status }) =>
			updateScheduledPostStatus(postId, status),
		onSuccess: (_, { postId }) => {
			// Invalidate all scheduled posts queries
			queryClient.invalidateQueries([SCHEDULED_POSTS_KEY]);
			// Also invalidate the specific post
			queryClient.invalidateQueries([SCHEDULED_POSTS_KEY, 'single', postId]);
		},
	});
};

export const useScheduledPostSeries = (postId) =>
	useQuery({
		queryKey: [SCHEDULED_POSTS_KEY, 'series', postId],
		queryFn: () => getScheduledPostSeries(postId).then((res) => res.data?.data),
		enabled: !!postId,
	});

export const useUpdateScheduledPostSeries = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ postId, data }) => updateScheduledPostSeries(postId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [SCHEDULED_POSTS_KEY] });
			queryClient.invalidateQueries(['aiWordpressGenerationUsage']);
		},
	});
};

// Delete a scheduled post
export const useDeleteScheduledPost = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ postId, scope = 'single' }) =>
			deleteScheduledPost(postId, { scope }),
		onSuccess: () => {
			queryClient.invalidateQueries([SCHEDULED_POSTS_KEY]);
		},
	});
};

// Get scheduled posts statistics
export const useScheduledPostsStats = (connectionId) =>
	useQuery({
		queryKey: [SCHEDULED_POSTS_KEY, connectionId, 'stats'],
		queryFn: () => getScheduledPostsStats(connectionId),
		enabled: !!connectionId,
	});

export const useConnectionHealth = (connectionId) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, connectionId, 'health'],
		queryFn: () => getConnectionHealth(connectionId).then((res) => res.data?.health),
		enabled: !!connectionId,
	});

export const useAiReadiness = (connectionId) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, connectionId, 'ai-readiness'],
		queryFn: () => getAiReadiness(connectionId).then((res) => res.data?.aiReadiness),
		enabled: !!connectionId,
	});

export const useDiscoveryCache = (connectionId) =>
	useQuery({
		queryKey: [CONNECTIONS_KEY, connectionId, 'discovery-cache'],
		queryFn: () => getDiscoveryCache(connectionId).then((res) => res.data),
		enabled: !!connectionId,
		retry: false,
	});

export const useSyncConnectionContent = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ connectionId, postType }) =>
			syncConnectionContent(connectionId, postType).then((res) => res.data),
		onSuccess: (_, { connectionId }) => {
			queryClient.invalidateQueries([CONNECTIONS_KEY, connectionId]);
			queryClient.invalidateQueries(['localContent', connectionId]);
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-readiness'],
			});
		},
	});
};

export const useAnalyzeConnectionContent = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (connectionId) =>
			analyzeConnectionContent(connectionId).then((res) => res.data),
		onSuccess: (_, connectionId) => {
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-summary'],
			});
			queryClient.invalidateQueries({
				queryKey: [CONNECTIONS_KEY, connectionId, 'ai-readiness'],
			});
		},
	});
};
